import { useEffect, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import {
  LandingForm as Form,
  LandingContext,
  InvalidTimeSlot,
  SplashImage,
  LandingView
} from 'shop/components/Landing'
import {
  useConsumerCart,
  useGoogleApis,
  useMerchant,
  useQueryParamAccountModal,
  useShop
} from 'shop/hooks'
import { format, isToday, parseISO } from 'date-fns'
import {
  getValidSamedayStores,
  getValidPreorderStores
} from 'shop/components/Landing/utils'
import { getStoreById } from '../utils'

import {
  ValidStore,
  Store,
  CurrentLocationState,
  AvailableDates,
  PartnerStoreBrowse,
  StoreRankingCriteria
} from 'shop/components/Landing/types'
import { Canonical, MetaDescription, PageTitle } from 'shop/components/SEO'
import { QUERY_VALID_STORES } from 'shop/client/queries/StoreQueries'
import { sortStoresByLocation } from 'shop/components/Landing/locationUtils'
import MediaQuery from 'react-responsive'
import { QUERY_GET_AVAILABLE_DATES } from 'shop/client/queries/DateQueries'
import {
  formatReplaceAvailableDates,
  getCalendarStartAndEndDates,
  getEarliestAvailableDate
} from 'shop/components/Landing/datePickerUtils'
import { usePageViewTracker } from 'tracker'
import { FulfillmentTypeLoadingStates } from 'shop/components/Landing/LandingContext'
import { DELIVERY_FULFILLMENT, PICKUP_FULFILLMENT } from 'shop/types'
import Cookies from 'shop/components/Landing/Cookies'
import BrowseStoresMap from 'shop/components/Maps/BrowseStoresMap'
import { MapInteracts } from 'shop/components/Maps/types'

interface LandingProps {
  modal?: any
  addToCart?: any
  cartId?: string
  storeId?: string
  orderAgainCartId?: string
  onClose: () => void
  view?: LandingView
}

const Landing = (props: LandingProps) => {
  const {
    config,
    useShopClient,
    partner,
    fetchPartnerAndCustomer,
    fetchSortedPartnerStores,
    customerDetails
  } = useShop()
  const {
    merchant,
    setMerchant,
    allStores,
    refetchMerchantAndStores,
    isMerchantLoading
  } = useMerchant()
  const { partnerId } = config
  const { seo } = partner || {}
  const seoDescription = seo?.description || ''

  const client = useShopClient()
  const { clearConsumerCart, cart: consumerCart } = useConsumerCart()
  const { areGoogleApisLoaded, getGoogleApis } = useGoogleApis()

  const [stores, setStores] = useState<Store[]>(allStores)
  const [currentStore, setCurrentStore] = useState<ValidStore>()
  const [validStores, setValidStores] = useState<ValidStore[]>([])
  const [loadingStores, setLoadingStores] =
    useState<FulfillmentTypeLoadingStates>({
      delivery: false,
      pickup: false
    })
  const [loadingDates, setLoadingDates] =
    useState<FulfillmentTypeLoadingStates>({
      delivery: false,
      pickup: false
    })
  const [fulfillmentType, setFulfillmentType] = useState<string>('')
  const [currentLocation, setCurrentLocation] = useState<CurrentLocationState>()
  const [fulfillmentDate, setFulfillmentDate] = useState<Date>(new Date())
  const [fulfillmentTime, setFulfillmentTime] = useState<string | null>(null)
  const [fulfillmentTimeRange, setFulfillmentTimeRange] = useState<
    string | null
  >(null)
  const [useCustomerAddress, setUseCustomerAddress] = useState(false)
  const [defaultLoadCustomerAddress, setDefaultLoadCustomerAddress] =
    useState(true)
  const [storeOutOfRange, setStoreOutOfRange] = useState<boolean>(false)
  const [sameDayOnly, setSameDayOnly] = useState<boolean>(false)
  const [preOrderOnly, setPreOrderOnly] = useState<boolean>(false)
  const [availableDates, setAvailableDates] = useState<AvailableDates>({
    pickup: {},
    delivery: {}
  })
  const [isExtendedHour, setIsExtendedHour] = useState(false)
  const [hasFailedSubmit, setHasFailedSubmit] = useState(false)
  const [showFulfillmentOptions, setShowFulfillmentOptions] = useState(false)
  const [formView, setFormView] = useState<LandingView | null>(null) // start as null to not render a view as default
  const [sortedPartnerStores, setSortedPartnerStores] = useState<
    PartnerStoreBrowse[]
  >([])
  const [sortCriteria, setSortCriteria] = useState<StoreRankingCriteria>({
    fulfillmentTypes: [DELIVERY_FULFILLMENT, PICKUP_FULFILLMENT]
  })
  const [skipPartnerQuery, setSkipPartnerQuery] = useState<boolean>(true)
  const [showMap, setShowMap] = useState<boolean>(props.view === 'browse')
  const [mapInteracts, setMapInteracts] = useState<MapInteracts>({
    currentlyViewedStore: null,
    currentlyHoveredStore: null
  })

  const hasAvailableDates: boolean = useMemo(() => {
    const availableMonths = Object.keys(availableDates[fulfillmentType] || {})
    if (!availableMonths.length) return false
    return true
  }, [availableDates, fulfillmentType])

  const includePickupAddress =
    fulfillmentType === 'pickup' && !!merchant?.pickup_address_enabled
  const includeAddressInVars =
    fulfillmentType === 'delivery' || includePickupAddress

  const getAvailableDates = (baseDate: Date = new Date()) => {
    if (fulfillmentType === '') return
    setLoadingDates({ ...loadingDates, [fulfillmentType]: true })

    const { startDate } = getCalendarStartAndEndDates(baseDate)

    return client
      .query({
        query: QUERY_GET_AVAILABLE_DATES,
        fetchPolicy: 'cache-first',
        variables: {
          merchantId: partnerId,
          fulfillmentType,
          startDate,
          lat: includeAddressInVars ? currentLocation?.latLng.lat : undefined,
          lng: includeAddressInVars ? currentLocation?.latLng.lng : undefined
        }
      })
      .then((res) => {
        setAvailableDates(
          formatReplaceAvailableDates(
            availableDates,
            res?.data?.dates || [],
            fulfillmentType
          )
        )
        setLoadingDates({ ...loadingDates, [fulfillmentType]: false })
      })
  }

  const landingState = {
    merchant,
    setMerchant,
    isMerchantLoading,
    stores,
    setStores,
    fulfillmentType,
    setFulfillmentType,
    fulfillmentDate,
    setFulfillmentDate,
    fulfillmentTime,
    setFulfillmentTime,
    fulfillmentTimeRange,
    setFulfillmentTimeRange,
    currentLocation,
    setCurrentLocation,
    currentStore,
    setCurrentStore,
    useCustomerAddress,
    setUseCustomerAddress,
    defaultLoadCustomerAddress,
    setDefaultLoadCustomerAddress,
    inModal: props.modal,
    addToCart: props.addToCart,
    cartId: props.cartId,
    orderAgainCartId: props.orderAgainCartId,
    validStores,
    loadingStores,
    setLoadingStores,
    loadingDates,
    setLoadingDates,
    storeOutOfRange,
    availableDates,
    getAvailableDates,
    isExtendedHour,
    setIsExtendedHour,
    sameDayOnly,
    preOrderOnly,
    hasFailedSubmit,
    setHasFailedSubmit,
    showFulfillmentOptions,
    setShowFulfillmentOptions,
    formView,
    setFormView,
    sortedPartnerStores,
    sortCriteria,
    setSortCriteria,
    mapInteracts,
    setMapInteracts
  }

  // Track page view
  usePageViewTracker()

  /** Load google maps on init */
  useEffect(() => {
    if (!areGoogleApisLoaded) {
      getGoogleApis()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Always clear consumer cart state when user visits the landing page
  useEffect(() => {
    const handleClearCart = () => {
      // Clear consumer cart state from context. localStorage value is cleared as part of MUTATE_REMOVE_CART call
      if (!!consumerCart?.id) clearConsumerCart()

      // also remove any other session storage helpers
      sessionStorage.removeItem('hasSelectedTip')
    }
    // to get around "bfcache" - doesn't trigger on other forms of nav / reloads.
    window.addEventListener('pageshow', handleClearCart)

    // handle clear for all other forms of nav / reloads.
    // this will result in a double call for "bfcache" flows, so keep handleClearCart light.
    handleClearCart()
    return () => {
      window.removeEventListener('pageshow', handleClearCart)
    }
  }, [])

  // Load login modal from query params
  useQueryParamAccountModal()

  // Update the stores state with the useShop allStores
  useEffect(() => {
    setStores(allStores)
  }, [allStores])

  // On load, update which form view to render
  useEffect(() => {
    setFormView(props.view || 'fulfillment')
    // every time the view changes refetch Partner data to keep stores up to date.
    fetchPartnerAndCustomer()
  }, [props.view]) // eslint-disable-line

  // Rehydrate the merchant and partner data on form view switch
  useEffect(() => {
    if (formView === 'fulfillment') {
      refetchMerchantAndStores()
    }
  }, [formView])

  useEffect(() => {
    if (!areGoogleApisLoaded) return
    // trigger the map on the browse view
    setShowMap(formView === 'browse')
  }, [areGoogleApisLoaded, formView])

  useEffect(() => {
    // only hydrate the sortedPartnerStores on the browse page.
    if (props.view === 'browse') {
      const customerId = localStorage.getItem('customerId')
      // if the customer has a default address and we are yet to set the currentLocation skip the query once.
      // this is so we don't double call the Partner query once the currentLocation gets set
      if (
        customerId &&
        !!customerDetails?.addresses?.find(
          (address) => address.isDefaultShippingAddress
        ) &&
        !currentLocation?.latLng &&
        skipPartnerQuery
      ) {
        setSkipPartnerQuery(false)
        return
      }

      let rankingCriteriaParams: StoreRankingCriteria = {
        ...sortCriteria
      }
      let sortByParams: ('RANK' | 'DISTANCE_FROM_POINT')[] = ['RANK']
      if (currentLocation?.latLng) {
        rankingCriteriaParams = {
          ...rankingCriteriaParams,
          coordinates: currentLocation.latLng
        }
        sortByParams.push('DISTANCE_FROM_POINT')
      }
      if (customerId) {
        rankingCriteriaParams = {
          ...rankingCriteriaParams,
          customerId: customerId
        }
      }

      fetchSortedPartnerStores({
        rankingCriteria: rankingCriteriaParams,
        sortDirection: 'ASCENDING',
        sortBy: sortByParams,
        fulfillmentDate: rankingCriteriaParams.fulfillmentDate,
        fulfillmentTime: rankingCriteriaParams.fulfillmentTime
      }).then((stores) => {
        setSortedPartnerStores(stores)
      })
    }
  }, [currentLocation?.latLng, sortCriteria, props.view]) // eslint-disable-line

  const clearSelectedTime = () => {
    setFulfillmentTime(null)
    setFulfillmentTimeRange(null)
  }

  const getValidStores = () => {
    const deliveryAddress =
      fulfillmentType === 'delivery' && currentLocation?.latLng
        ? {
            geom: {
              lat: currentLocation.latLng.lat.toString(),
              long: currentLocation.latLng.lng.toString()
            },
            type: 'GEOCODE'
          }
        : null
    return client
      .query({
        query: QUERY_VALID_STORES,
        variables: {
          merchantId: partnerId,
          fulfillmentType,
          fulfillmentDate: format(fulfillmentDate, 'yyyy-MM-dd'),
          deliveryAddress
        }
      })
      .then(({ data: { getValidStores } }) => {
        const { stores } = getValidStores[0]
        // Doing this for now
        const filteredStores = stores.filter(
          (store: ValidStore) => store.fulfillmentTimeslots.length > 0
        )
        if (currentLocation)
          return sortStoresByLocation(filteredStores, currentLocation)
        return filteredStores
      })
  }

  /**
   * When switching fulfillmentType set to earliest available date,
   * if no available date, call getAvailableDates
   */
  useEffect(() => {
    // set to earliest available date of already fetched available dates.
    if (hasAvailableDates) {
      const earliestDate = getEarliestAvailableDate(
        availableDates,
        fulfillmentType
      )
      setFulfillmentDate(earliestDate)
      return
    }
    // otherwise call getAvailableDates
    if (fulfillmentType === 'delivery' && currentLocation) getAvailableDates()
    if (fulfillmentType === 'pickup') getAvailableDates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fulfillmentType])

  /**
   * Whenever a user changes location, fetch new available dates
   */
  useEffect(() => {
    if (currentLocation) getAvailableDates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation])

  // whenever we finish fetching available dates, set to earliest fulfillment date
  useEffect(() => {
    // no dates mean no stores
    if (!hasAvailableDates) setValidStores([])
    if (!loadingDates.delivery && !loadingDates.pickup && hasAvailableDates) {
      const earliestDate = getEarliestAvailableDate(
        availableDates,
        fulfillmentType
      )
      if (earliestDate) {
        setFulfillmentDate(earliestDate)
        return
      }
      setFulfillmentDate(new Date())
    }
  }, [loadingDates])

  useEffect(() => {
    const isDelivery = fulfillmentType === 'delivery'
    const hasAddress = isDelivery ? !!currentLocation?.address : true
    if (
      merchant &&
      fulfillmentDate &&
      fulfillmentType &&
      hasAddress &&
      hasAvailableDates
    ) {
      setLoadingStores({ ...loadingStores, [fulfillmentType]: true })
      setValidStores([])
      getValidStores()
        .then((stores) => {
          setValidStores(stores || [])
          setDefaultStore(stores)
        })
        .catch(console.error)
        .finally(() => {
          setLoadingStores({ ...loadingStores, [fulfillmentType]: false })
          clearSelectedTime()
        })
    }
  }, [fulfillmentDate]) // eslint-disable-line

  const setDefaultStore = (stores: ValidStore[]) => {
    const { orderAgainCartId, storeId } = props
    if (orderAgainCartId) {
      const storeById = getStoreById(stores, storeId)
      setCurrentStore(storeById)
    } else {
      const [firstStore] = stores
      if (firstStore) setCurrentStore(firstStore)
    }
  }

  useEffect(() => {
    if (!areGoogleApisLoaded) return
    if (merchant && fulfillmentType) {
      const formattedDate = parseISO(fulfillmentDate.toISOString())

      if (isToday(formattedDate)) {
        const { storeOutOfRange: sameDayStoreOutOfRange } =
          getValidSamedayStores(
            stores,
            fulfillmentType,
            formattedDate,
            currentLocation
          )
        setStoreOutOfRange(sameDayStoreOutOfRange)
      } else {
        const { storeOutOfRange: preOrderStoreOutOfRange } =
          getValidPreorderStores(
            stores,
            fulfillmentType,
            formattedDate,
            currentLocation
          )
        setStoreOutOfRange(preOrderStoreOutOfRange)
      }
    }
  }, [
    currentLocation,
    fulfillmentType,
    fulfillmentDate,
    stores,
    merchant,
    areGoogleApisLoaded
  ])

  useEffect(() => {
    if (!areGoogleApisLoaded) return
    if (merchant && stores) {
      const { validStores: availableSameDayStores } = getValidSamedayStores(
        stores,
        fulfillmentType,
        new Date(),
        currentLocation
      )
      const { validStores: availableStoresForPreorder } =
        getValidPreorderStores(
          stores,
          fulfillmentType,
          undefined,
          currentLocation
        )

      setSameDayOnly(false)
      setPreOrderOnly(false)

      if (
        availableStoresForPreorder.length > 0 &&
        availableSameDayStores.length === 0
      ) {
        setPreOrderOnly(true)
      }
      if (
        availableStoresForPreorder.length === 0 &&
        availableSameDayStores.length > 0
      ) {
        setSameDayOnly(true)
      }
    }
  }, [currentLocation, stores, merchant, fulfillmentType, areGoogleApisLoaded])

  // Attempts to clear timeslot upon store change
  useEffect(() => {
    if (fulfillmentTime) clearSelectedTime()
  }, [currentStore])

  return (
    <LandingContext.Provider value={landingState}>
      <Canonical url={window.location.origin + window.location.pathname} />
      <PageTitle />
      <MetaDescription seoDescription={seoDescription} type='merchant' />
      <Container>
        {!props.modal && !window.localStorage.getItem('closeNotice') && (
          <CookiesContainer>
            <Cookies />
          </CookiesContainer>
        )}
        <FormContainer>
          <Form onClose={props.onClose} />
        </FormContainer>
        {!props.modal && (
          <MediaQuery minWidth={768.1}>
            <SplashContainer>
              {showMap ? <BrowseStoresMap /> : <SplashImage />}
            </SplashContainer>
          </MediaQuery>
        )}
      </Container>
      <InvalidTimeSlot />
    </LandingContext.Provider>
  )
}

const Container = styled.div(({ theme }) => ({
  position: 'relative',
  height: '100%',
  minHeight: '100vh',
  '& > *:only-child': {
    minHeight: '100%',
    width: '100%',
    margin: 0
  },
  [theme.mediaQueries.viewport7]: {
    minHeight: 'auto',
    backgroundColor: 'white'
  }
}))

const CookiesContainer = styled.div(({ theme }) => ({
  zIndex: 15,
  left: 0,
  right: 0,
  top: 0,
  position: 'relative',
  [theme.mediaQueries.viewport7]: {
    padding: '24px',
    left: '45%',
    width: '50%',
    top: 'auto',
    position: 'fixed',
    bottom: 0
  },
  [theme.mediaQueries.viewport9]: {
    left: '40%'
  }
}))

const FormContainer = styled.div(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  padding: '0',
  minHeight: '100vh',
  [theme.mediaQueries.viewport7]: {
    padding: '0',
    width: '45%',
    backgroundColor: 'white'
  },
  [theme.mediaQueries.viewport9]: {
    width: '40%'
  }
}))

const SplashContainer = styled.div(({ theme }) => ({
  position: theme.designMode ? 'absolute' : 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  minHeight: '360px',
  height: '40vh',
  [theme.mediaQueries.viewport7]: {
    left: '45%',
    bottom: 0,
    height: '100vh'
  },
  [theme.mediaQueries.viewport9]: {
    left: '40%'
  }
}))

export default Landing
